import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationContext";
import { useFloatingTools } from "@/context/FloatingToolsContext";
import Logo from "@/components/Logo";
import {
  House, Phone, Users, ListBullets, Microphone, ChatText, CalendarBlank,
  Hash, UsersThree, ChartBar, FlowArrow, Gear, SignOut, Sparkle, Trophy, CreditCard,
  Headphones, Note, FileText, Calculator as CalcIcon,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/app", label: "Dashboard", icon: House, end: true },
  { to: "/app/dialer", label: "Dialer", icon: Phone },
  { to: "/app/contacts", label: "Contacts", icon: Users },
  { to: "/app/calls", label: "Call Logs", icon: ListBullets },
  { to: "/app/recordings", label: "Recordings", icon: Microphone },
  { to: "/app/sms", label: "Messages", icon: ChatText },
  { to: "/app/meetings", label: "Meetings", icon: CalendarBlank, businessOnly: true },
  { to: "/app/numbers", label: "Numbers", icon: Hash, roles: ["admin"] },
  { to: "/app/team", label: "Team", icon: UsersThree, roles: ["admin", "supervisor"], businessOnly: true },
  { to: "/app/leaderboard", label: "Leaderboard", icon: Trophy, roles: ["admin", "supervisor"], businessOnly: true },
  { to: "/app/coaching", label: "Live Monitoring", icon: Headphones, roles: ["admin", "supervisor"], businessOnly: true },
  { to: "/app/analytics", label: "Analytics", icon: ChartBar, roles: ["admin", "supervisor"] },
  { section: "Tools" },
  { label: "Notepad",      icon: FileText, tool: "notepad"    },
  { label: "Sticky Notes", icon: Note,     tool: "notes"      },
  { label: "Calculator",   icon: CalcIcon, tool: "calculator" },
  { section: null },  // divider end
  { to: "/app/billing", label: "Billing", icon: CreditCard, roles: ["admin"] },
  { to: "/app/ivr", label: "IVR Builder", icon: FlowArrow, roles: ["admin"] },
  { to: "/app/settings", label: "Settings", icon: Gear },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { unreadTotal, smsUnread } = useNotifications();
  const totalMessagesUnread = (unreadTotal || 0) + (smsUnread || 0);
  const nav = useNavigate();
  const { openTool } = useFloatingTools();

  return (
    <aside data-testid="sidebar" className="hv-side">
      <div className="hv-side__panel">
        <div className="px-7 pt-7 pb-5 border-b border-slate-50">
          <Logo size={60} />
          <div className="font-mono text-[9px] text-slate-500 mt-3 tracking-[.18em] uppercase truncate">
            {user?.organization?.name
              ? <>{user.organization.name}{user.organization.plan ? <span className="text-slate-400"> · {user.organization.plan.toUpperCase()}</span> : ""}</>
              : "VOICE · CONNECT · GROW"}
          </div>
        </div>

        <nav className="flex-1 py-3 overflow-y-auto">
          {NAV
            .filter((n) => !n.roles || n.roles.includes(user?.role))
            .filter((n) => !n.businessOnly || user?.organization?.type !== "individual")
            .map((item, idx) => {
              // Section divider (e.g. "Tools"): renders a small label, no link.
              if ("section" in item) {
                if (!item.section) return <div key={`div-${idx}`} className="my-3 border-b border-slate-100 mx-6" />;
                return (
                  <div key={`sec-${idx}`} className="hv-side__section">
                    {item.section}
                  </div>
                );
              }
              // Tool entries don't navigate — they open a floating mini-window.
              if (item.tool) {
                const Icon = item.icon;
                return (
                  <button
                    key={`tool-${item.tool}`}
                    onClick={() => openTool(item.tool)}
                    className="hv-nav w-full text-left"
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    <span className="hv-nav__icon"><Icon size={16} weight="regular" /></span>
                    <span className="flex-1">{item.label}</span>
                  </button>
                );
              }
              const { to, label, icon: Icon, end } = item;
              return (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) => `hv-nav ${isActive ? "active" : ""}`}
                >
                  <span className="hv-nav__icon"><Icon size={16} weight="regular" /></span>
                  <span className="flex-1">{label}</span>
                  {label === "Messages" && totalMessagesUnread > 0 && (
                    <span className="hv-badge" data-testid="sidebar-messages-unread">
                      {totalMessagesUnread > 99 ? "99+" : totalMessagesUnread}
                    </span>
                  )}
                </NavLink>
              );
            })}
        </nav>

      {user?.organization?.type === "individual" && (
        <NavLink
          to="/app/settings"
          data-testid="sidebar-upgrade-cta"
          className="cta-shimmer mx-4 mb-2 group relative overflow-hidden rounded-2xl shadow-lg shadow-[#5B5FC7]/25 hover:shadow-xl hover:shadow-[#5B5FC7]/35 transition-all duration-300 hover:-translate-y-0.5"
          style={{
            background: "linear-gradient(135deg, #5B5FC7 0%, #7A7FE0 55%, #A78BFA 100%)",
          }}
        >
          {/* Soft radial highlight */}
          <div className="pointer-events-none absolute -top-4 -right-4 w-20 h-20 rounded-full bg-white/15 blur-2xl" aria-hidden="true" />

          <div className="relative p-3.5 text-white">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-white/20 backdrop-blur flex items-center justify-center ring-1 ring-white/30">
                <Sparkle size={12} weight="fill" />
              </div>
              <span className="font-mono text-[9.5px] tracking-[0.18em] font-bold opacity-95">
                BUSINESS PLAN
              </span>
            </div>

            <div className="font-display font-extrabold text-[14px] mt-2 leading-snug">
              Unlock the full toolkit
            </div>

            <ul className="mt-2 space-y-0.5 text-[11.5px] opacity-90">
              <li className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-white/80" /> Team Chat & Voice Notes</li>
              <li className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-white/80" /> Group Meetings & Calls</li>
              <li className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-white/80" /> Unlimited Teammates</li>
            </ul>

            <div className="mt-3 pt-2 border-t border-white/15 flex items-center justify-between">
              <span className="text-[10.5px] font-mono opacity-80">Upgrade in 30s</span>
              <span className="text-[11px] font-bold tracking-wide">
                Switch <span className="cta-arrow">→</span>
              </span>
            </div>
          </div>
        </NavLink>
      )}

      <div className="hv-side__user">
        <img
          src={user?.avatar_url}
          alt={user?.name}
          data-testid="sidebar-user-avatar"
        />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="text-[10.5px] text-[var(--text-muted)] tracking-wider truncate">{user?.role?.toUpperCase()}</div>
        </div>
        <button
          onClick={async () => { await logout(); nav("/login"); }}
          className="p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-white transition-colors"
          title="Log out"
          data-testid="logout-btn"
        >
          <SignOut size={16} />
        </button>
      </div>
      </div>
    </aside>
  );
}
